"use client";
import React, { useState, useEffect } from "react";

import AppImage from "@/components/ui/AppImage";
import { useHomeLanguage } from "@/app/home/components/HomeLanguageContext";

export default function Footer() {
  const { lang } = useHomeLanguage();
  const [year, setYear] = useState("2024");

  useEffect(() => {
    setYear(new Date()?.getFullYear()?.toString());
  }, []);

  const labels = {
    about: lang === "id" ? "Tentang" : "About",
    features: lang === "id" ? "Fitur" : "Features",
    pricing: lang === "id" ? "Harga" : "Pricing",
    trial: lang === "id" ? "Coba Gratis" : "Try Free",
    contact: lang === "id" ? "Kontak" : "Contact",
  };

  return (
    <footer className="bg-white border-t border-stone-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-9 sm:py-12 lg:py-14">
        {/* Top row */}
        <div className="flex flex-col md:flex-row items-center md:items-center justify-between gap-7 sm:gap-8 md:gap-10 pb-8 sm:pb-9 md:pb-10 border-b border-stone-200">
          {/* Brand */}
          <div className="flex flex-col gap-3 items-center md:items-start text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start">
              <AppImage
                src="/assets/optimized/logolangit-8cdc10411c.webp"
                alt="PT. Langit Anantara Kreasi logo"
                width={190}
                height={54}
                className="h-8 w-auto opacity-80" />
            </div>
            <p className="text-fg-muted text-[12px] max-w-[240px] leading-relaxed">
              Kolabo.id - PT. Langit Anantara Kreasi
            </p>
          </div>

          {/* Nav links */}
          <div className="flex flex-wrap items-center justify-center gap-x-7 sm:gap-x-8 gap-y-3 text-[12px] text-center max-w-[260px] sm:max-w-none">
            <a href="#about" className="text-fg-muted hover:text-fg transition-colors font-medium tracking-wide">{labels.about}</a>
            <a href="#features" className="text-fg-muted hover:text-fg transition-colors font-medium tracking-wide">{labels.features}</a>
            <a href="#pricing" className="text-fg-muted hover:text-fg transition-colors font-medium tracking-wide">{labels.pricing}</a>
            <a href="#daftar" className="text-fg-muted hover:text-fg transition-colors font-medium tracking-wide">{labels.trial}</a>
            <a href="#contact" className="text-fg-muted hover:text-fg transition-colors font-medium tracking-wide">{labels.contact}</a>
          </div>

          {/* Social */}
          <div className="flex items-center justify-center gap-2.5">
            <a href="https://instagram.com/kolabo.id" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-md bg-stone-50 border border-stone-200 flex items-center justify-center text-fg/45 hover:text-fg hover:bg-primary/10 hover:border-primary/30 transition-all">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="20" height="20" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" /></svg>
            </a>
            <a href="https://wa.me/6282125886584" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-md bg-stone-50 border border-stone-200 flex items-center justify-center text-fg/45 hover:text-fg hover:bg-cta/15 hover:border-cta/30 transition-all">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
            </a>
          </div>
        </div>
        {/* Bottom row */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4 pt-6 sm:pt-8 pb-[max(5.25rem,env(safe-area-inset-bottom))] sm:pb-0 text-[12px] sm:text-[13px] text-fg/45 tracking-wide text-center sm:text-left">
          <span className="w-full text-center sm:text-left">© {year} Kolabo.id - PT. Langit Anantara Kreasi</span>
          <div className="w-full sm:w-auto flex flex-wrap items-center justify-center sm:justify-end gap-x-4 gap-y-2 sm:gap-5">
            <a href="https://www.langitcreative.solutions/" className="hover:text-fg transition-colors">Langit Creative</a>
            <span className="hidden sm:inline text-fg/25">·</span>
            <a href="https://www.kolabo.id/privacy-policy.html" className="hover:text-fg transition-colors">Privacy Policy</a>
            <span className="hidden sm:inline text-fg/25">·</span>
            <a href="https://www.kolabo.id/terms-n-condition.html" className="hover:text-fg transition-colors">Terms &amp; Condition</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
